/**
 * CirclePage — the parent Resources page (`/circle`; the href and the
 * impersonation menu key `circle` never rename).
 *
 * Two things, in this order (wave w1023c, Victor 2026-10-23, "clear is
 * kind"): "Your TRF contact" — who to write to and when they will hear back —
 * then the resources TRF curated for this family (`ResourcesSection`).
 *
 * The People address book that used to fill this page (contacts list,
 * add/edit/remove, CSV export, #2437) was deleted in the same wave, with its
 * `/api/circle` contacts routes and the companion's three support-circle
 * tools. The stored `support_circle` rows stay in Mongo; nothing reads them
 * here.
 */
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { MonoLabel, PendingDecision } from '@/components/editorial';
import { cn } from '@/lib/utils';
import { useFamilyMine } from '@/hooks/useFamilyMine';
import { TONE_CLASSES, TRF_CONTACT_REPLY_BUSINESS_DAYS, initials } from '@/lib/circle-display';
import { CRISIS_PARAMS } from '@/features/chat-trf/crisis/resources';

import { CircleHeader } from '@/components/circle/CircleChrome';
import { ResourcesSection } from '@/components/circle/ResourcesSection';

// ── Your TRF contact (relocated from the sidebar identity block, #844) ──────
//
// `FamilyMine.assigned_trfer` carries the assigned TRFer's name and sign-in
// email. The card shows the name and a mailto line. #2956: the generated mock
// email and (555) phone were deleted because a family could reach no one with
// them; the real address replaces them. No phone: there is no opt-in for one.
// Which address families should see is an open decision, marked (?).

/**
 * The not-an-emergency-service lines under the TRF contact (wave w1023c).
 *
 * TRF is not a crisis line (#2566): the card names 911 and 988 and never TRF
 * staff or the companion. The 911 and 988 sentences are `CrisisCallout`'s own
 * `crisis:callout.*` keys and `CRISIS_PARAMS`, so the per-language 988 route
 * (es: press 2 or text AYUDA; fr, pt, zh: ask for an interpreter) is the one
 * every other crisis surface shows. There is no second wording to drift.
 */
function NotAnEmergencyService() {
  const { t } = useTranslation(['circle', 'crisis']);
  const call = (number: string) => (
    <a href={`tel:${number}`} className="font-medium text-foreground underline underline-offset-2" />
  );
  return (
    <div data-testid="trf-contact-not-emergency" className="space-y-1 text-body-sm text-stevens-ink-3">
      <p>{t('circle:caseManager.notEmergency')}</p>
      <p>
        <Trans
          t={t}
          i18nKey="crisis:callout.emergency"
          values={CRISIS_PARAMS}
          components={{ call: call(CRISIS_PARAMS.emergency), detail: <span /> }}
        />
      </p>
      <p>
        <Trans
          t={t}
          i18nKey="crisis:callout.lifeline"
          values={CRISIS_PARAMS}
          components={{ call: call(CRISIS_PARAMS.lifeline), detail: <span /> }}
        />
      </p>
    </div>
  );
}

/**
 * "Your TRF contact" (wave w1023c, Victor 2026-10-23): the first thing a
 * parent reads on Resources is who to write to and when they will hear back.
 * The `caseManager.*` keys keep their names; only the words changed.
 */
function TrfContactCard() {
  const { t } = useTranslation('circle');
  const { data, isLoading } = useFamilyMine();
  // Hold while the family record is in flight — rendering the "not assigned
  // yet" empty state first and swapping it for a name would be a flash of
  // wrong information, the same fail-safe direction the sidebar gates use.
  if (isLoading) return null;

  const trferName = data?.data?.assigned_trfer?.name?.trim();
  const trferEmail = data?.data?.assigned_trfer?.email?.trim() || null;
  const tone = TONE_CLASSES.indigo; // identity mark, not a status

  return (
    <section aria-label={t('caseManager.title')} className="space-y-2">
      <div className="flex items-center gap-1.5">
        <MonoLabel className="block">{t('caseManager.title')}</MonoLabel>
        <PendingDecision id="case-manager-contact-address" side="right" />
      </div>
      <Card className="shadow-none">
        <CardContent className="space-y-3 px-5 py-4">
          {trferName ? (
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'font-serif inline-flex items-center justify-center w-11 h-11 rounded-full font-medium text-base flex-shrink-0',
                  tone.avatarBg,
                  tone.text,
                )}
              >
                {initials(trferName)}
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <h2 translate="no" className="font-serif text-card-title font-medium leading-tight text-foreground tracking-tight truncate">
                  {trferName}
                </h2>
                {trferEmail && (
                  <a
                    href={`mailto:${trferEmail}`}
                    translate="no"
                    className="block break-all text-body-sm text-stevens-ink-3 underline underline-offset-2"
                  >
                    {trferEmail}
                  </a>
                )}
                <p className="text-body-sm text-foreground">
                  {t('caseManager.replyPromise', { count: TRF_CONTACT_REPLY_BUSINESS_DAYS })}
                </p>
              </div>
            </div>
          ) : (
            // Graceful empty state — true platform state (no TRFer assigned).
            <p className="text-body text-stevens-ink-3">{t('caseManager.none')}</p>
          )}
          <NotAnEmergencyService />
        </CardContent>
      </Card>
    </section>
  );
}

export function CirclePage() {
  return (
    <div className="space-y-5">
      <CircleHeader />
      <TrfContactCard />
      <ResourcesSection />
    </div>
  );
}
